/**
 * Label for the pointer "slot" holding a container's default canonical document. It is used two ways:
 * as the final segment of the pointer path (`.../slots/<label>`) and as the value written to
 * the winning document's `canonical` field. It is a slot label, NOT the document's `type`: multiple
 * canonical documents of the same type can coexist in one container, differentiated by their label.
 * firestore.rules reads the label from the document's `canonical` field, so it is not hardcoded there.
 */
export const kDefaultCanonicalDocumentLabel = "default";
/**
 * Path (relative to the Firestore root) of a canonical-document pointer. Pointers live in a dedicated,
 * versioned collection — `canonical/v1/…` — rather than interleaved in the class/offering document tree.
 * That keeps their access rules stated per concern (uniform read + immutability across containers, create
 * per container shape) and leaves room to introduce a different layout under a later version prefix.
 * Below the version:
 *
 *   canonical/v1/classes/<classHash> / [offerings/<offeringId> | units/<unit>] / owners/<uid> / slots/<label>
 *
 * The class leads because the rules authorize reads by comparing that segment to the caller's class
 * claim, which a synthetic group owner id cannot supply. Below it the container contributes its **own**
 * level and id rather than a chain of ancestors: an offering names only itself, even though every
 * offering falls inside a classUnit. Pointers are immutable (firestore.rules grants no update or
 * delete), so a pointer cannot be moved — addressing each one by its own container is what lets a new
 * container level be introduced later without stranding the pointers that already exist. A document
 * kept by the class alone would omit the level entirely; nothing creates one yet, and the rules grant
 * no create for that shape (see firestore.rules).
 *
 * firestore.rules `canonicalPointerPath` builds the same path the same way (string concat + path(),
 * verified against the emulator) — keep the two in lockstep.
 */
export function getCanonicalPointerPath(slot) {
    const { classHash, offeringId, unit, owner, label } = slot;
    const segments = [`canonical/v1/classes/${classHash}`];
    if (offeringId) {
        segments.push(`offerings/${offeringId}`);
    }
    else if (unit) {
        segments.push(`units/${unit}`);
    }
    segments.push(`owners/${owner}`);
    segments.push(`slots/${label}`);
    return segments.join("/");
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoic2NvcGVkLWRvY3VtZW50LXBvaW50ZXJzLmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vLi4vLi4vc3JjL2xpYi9zY29wZWQtZG9jdW1lbnQtcG9pbnRlcnMudHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IkFBUUE7Ozs7OztHQU1HO0FBQ0gsTUFBTSxDQUFDLE1BQU0sOEJBQThCLEdBQUcsU0FBUyxDQUFDO0FBbUJ4RDs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7R0FvQkc7QUFDSCxNQUFNLFVBQVUsdUJBQXVCLENBQUMsSUFBb0I7SUFDMUQsTUFBTSxFQUFFLFNBQVMsRUFBRSxVQUFVLEVBQUUsSUFBSSxFQUFFLEtBQUssRUFBRSxLQUFLLEVBQUUsR0FBRyxJQUFJLENBQUM7SUFDM0QsTUFBTSxRQUFRLEdBQUcsQ0FBQyx3QkFBd0IsU0FBUyxFQUFFLENBQUMsQ0FBQztJQUN2RCxJQUFJLFVBQVUsRUFBRSxDQUFDO1FBQ2YsUUFBUSxDQUFDLElBQUksQ0FBQyxhQUFhLFVBQVUsRUFBRSxDQUFDLENBQUM7SUFDM0MsQ0FBQztTQUFNLElBQUksSUFBSSxFQUFFLENBQUM7UUFDaEIsUUFBUSxDQUFDLElBQUksQ0FBQyxTQUFTLElBQUksRUFBRSxDQUFDLENBQUM7SUFDakMsQ0FBQztJQUNELFFBQVEsQ0FBQyxJQUFJLENBQUMsVUFBVSxLQUFLLEVBQUUsQ0FBQyxDQUFDO0lBQ2pDLFFBQVEsQ0FBQyxJQUFJLENBQUMsU0FBUyxLQUFLLEVBQUUsQ0FBQyxDQUFDO0lBQ2hDLE9BQU8sUUFBUSxDQUFDLElBQUksQ0FBQyxHQUFHLENBQUMsQ0FBQztBQUM1QixDQUFDIn0=